import { useState, useRef } from 'react';
import { Box, Typography, Theme } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { makeStyles } from 'tss-react/mui';
import { AxiosError } from 'axios';
import SaveIcon from '@mui/icons-material/Save';
import DownloadIcon from '@mui/icons-material/Download';
import FormSubmitButton from '../../components/buttons/FormSubmitButton';
import FormCancelButton from '../../components/buttons/FormCancelButton';
import FormFileUploadField from '../../components/formFields/FormFileUploadField';
import useSnackBar from '../../hooks/useSnackBar';
import { vendorBulkUploadDocument, getVendorBulkUploadTemplateUrl } from '../../api/vendorApi/vendorAccountsApi';
import VendorBulkTable from '../VendorsView/VendorBulkTable';
import { ErrorData } from '../../types';

const useStyles = makeStyles()((theme: Theme) => ({
    vendorCreateForm: {
        padding: '32px',
        flexBasis: '896px',
        backgroundColor: theme.palette.primary.light,
        borderRadius: '16px',
        width: '100%',
        maxWidth: '896px',

        '@media (max-width: 1100px)': {
            flexBasis: 'auto',
            maxWidth: '100%',
        },

        '@media (max-width: 725px)': {
            padding: '16px',
        },
    },
    headerRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '8px',
    },
    fieldHeadline: {
        fontWeight: 600,
        fontSize: '20px',
        lineHeight: '24px',
        color: theme.palette.common.white,
        fontFamily: 'Inter',
    },
    sampleDownloadLink: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '16px',
        fontWeight: 600,
        fontFamily: 'Inter',
        color: theme.palette.info.main,
        cursor: 'pointer',
        userSelect: 'none',
        '&:hover': {
            textDecoration: 'underline',
        },
        '& svg': {
            fontSize: '20px',
        },
    },
    formWrapper: {
        display: 'flex',
        width: '100%',
    },
}));

// Form interface
interface VendorFileUploadForm {
    uploadedFile?: File | null;
}

const CreateBulkVendorContainer = () => {
    const styles = useStyles();
    const { setAlert } = useSnackBar();

    const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(false);
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [fileKey, setFileKey] = useState<number>(Date.now());
    const tableRef = useRef<{ refresh: () => void }>(null);

    const formMethods = useForm<VendorFileUploadForm>({
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: {
            uploadedFile: undefined,
        },
    });

    const {
        handleSubmit,
        reset,
        setValue,
    } = formMethods;

    const onSubmitForm = (values: VendorFileUploadForm) => {
        const uploadedFile = values.uploadedFile;

        if (!uploadedFile) {
            setAlert((prev) => ({
                ...prev,
                message: 'Please upload an Excel file.',
                type: 'error',
            }));
            return;
        }

        setIsFormSubmitting(true);

        vendorBulkUploadDocument(uploadedFile)
            .then((response) => {
                setIsFormSubmitting(false);

                setAlert((prev) => ({
                    ...prev,
                    message: response.data?.message || 'Excel file uploaded successfully.',
                    type: 'success',
                }));

                reset({ uploadedFile: undefined });
                setUploadedFile(null);
                setFileKey(Date.now());
                tableRef.current?.refresh();
            })
            .catch((error: AxiosError<ErrorData>) => {
                setIsFormSubmitting(false);

                const errorMessage = error.response?.data?.message || 'Failed to upload Excel file.';

                setAlert((prev) => ({
                    ...prev,
                    message: errorMessage,
                    type: 'error',
                }));
            });
    };

    const onCancel = () => {
        reset({ uploadedFile: undefined });
        setUploadedFile(null);
        setFileKey(Date.now());
    };

    const onDownloadSample = () => {
        getVendorBulkUploadTemplateUrl()
            .then((response) => {
                const downloadUrl = response.data?.downloadUrl;

                if (!downloadUrl) {
                    setAlert((prev) => ({
                        ...prev,
                        message: 'Sample template is not available.',
                        type: 'error',
                    }));
                    return;
                }

                window.open(downloadUrl, '_blank', 'noopener,noreferrer');
            })
            .catch((error: AxiosError<ErrorData>) => {
                const errorMessage = error.response?.data?.message || 'Failed to download sample template.';

                setAlert((prev) => ({
                    ...prev,
                    message: errorMessage,
                    type: 'error',
                }));
            });
    };

    return (
        <>
            <Box className={styles.classes.formWrapper}>
                <FormProvider {...formMethods}>
                    <form className={styles.classes.vendorCreateForm} onSubmit={handleSubmit(onSubmitForm)}>
                        <Box className={styles.classes.headerRow}>
                            <Typography className={styles.classes.fieldHeadline}>
                                Upload Excel File
                            </Typography>

                            <Box
                                className={styles.classes.sampleDownloadLink}
                                onClick={onDownloadSample}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        onDownloadSample();
                                    }
                                }}
                            >
                                <DownloadIcon />
                                Sample Template
                            </Box>
                        </Box>

                        <Box mt="16px">
                            <FormFileUploadField
                                key={fileKey}
                                uploadLabel="Upload Excel File"
                                uploadText="Click to upload or drag and drop"
                                uploadSubText="Excel files only (.xlsx) - Max 10MB"
                                acceptedTypes={[
                                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                                ]}
                                maxFileSize={10 * 1024 * 1024}
                                onFileChange={(file) => {
                                    setUploadedFile(file);
                                    setValue('uploadedFile', file, { shouldValidate: true });
                                }}
                                value={uploadedFile}
                                inputId="excel-file-input"
                            />
                        </Box>

                        <Box display="flex" flexDirection="row" gap="16px" mt="32px" justifyContent="center">
                            <Box maxWidth="200px" width="100%">
                                <FormSubmitButton
                                    disabled={!uploadedFile || isFormSubmitting}
                                    buttonIcon={<SaveIcon />}
                                    buttonText={isFormSubmitting ? 'Creating...' : 'Create'}
                                    onClick={handleSubmit(onSubmitForm)}
                                />
                            </Box>
                            <Box maxWidth="200px" width="100%">
                                <FormCancelButton
                                    disabled={!uploadedFile || isFormSubmitting}
                                    onClick={onCancel}
                                />
                            </Box>
                        </Box>
                    </form>
                </FormProvider>
            </Box>
            <VendorBulkTable ref={tableRef} />
        </>
    );
};

export default CreateBulkVendorContainer;